import React, { useCallback, useMemo } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { ReopenManualRoom } from '~/types/reopen';
import { ExistingBlackoutDate } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';

import AddDateSection from './AddDateSection';
import ReopenManualDateRow from './ReopenManualDateRow';

interface Props {
  room: ReopenManualRoom;
  roomIndex: number;
  blackoutDates: Array<ExistingBlackoutDate>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  deselectedBlockIds: Set<string>;
  onAddDate: (roomIndex: number, start: string, end: string) => void;
  onEditDate: (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => void;
  onRemoveRoom: (roomIndex: number) => void;
  onRemoveDate: (roomIndex: number, dateId: string) => void;
  onToggleDate: (roomIndex: number, dateId: string, selected: boolean) => void;
  onBlockToggle: (blockId: string, selected: boolean) => void;
}

export default function ReopenManualRoomCard({
  room,
  roomIndex,
  blackoutDates,
  catalogRoomRates,
  deselectedBlockIds,
  onAddDate,
  onEditDate,
  onRemoveRoom,
  onRemoveDate,
  onToggleDate,
  onBlockToggle,
}: Props) {
  const handleRemoveRoom = useCallback(() => onRemoveRoom(roomIndex), [onRemoveRoom, roomIndex]);

  const handleAddDate = useCallback(
    (start: string, end: string) => onAddDate(roomIndex, start, end),
    [onAddDate, roomIndex],
  );

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackouts = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackouts.length > 0 ? futureBlackouts.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack gap={0.5}>
          <Typography fontWeight={500}>{room.catalogRoomName}</Typography>
          {existingLabel && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <LockOpenOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Existing blackouts: {existingLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
        <IconButton size="small" onClick={handleRemoveRoom}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack gap={0.5} mb={1}>
        <AddDateSection onAdd={handleAddDate} />
      </Stack>

      {room.dates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dates added yet
        </Typography>
      ) : (
        <Stack gap={0.5}>
          {room.dates.map((date) => (
            <ReopenManualDateRow
              key={date.id}
              date={date}
              roomIndex={roomIndex}
              catalogRoomRates={catalogRoomRates}
              deselectedBlockIds={deselectedBlockIds}
              onToggle={onToggleDate}
              onEdit={onEditDate}
              onDelete={onRemoveDate}
              onBlockToggle={onBlockToggle}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
